var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var BaseForecastConfig_1;
import { BeforeDeserialized, JSON, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { RegressorPerformanceMeasureMethod } from './baseRegressorConfig';
import { ToolConfig } from './toolConfig';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
import { baseForecastConfigSchemaValidation } from '../validations.generated';
export var ForecastFrequency;
(function (ForecastFrequency) {
    ForecastFrequency["Daily"] = "D";
    ForecastFrequency["Weekly"] = "W-MON";
    ForecastFrequency["Monthly"] = "MS";
    ForecastFrequency["Yearly"] = "AS-JAN";
    ForecastFrequency["Quarterly"] = "QS-";
    ForecastFrequency["BestGuess"] = "BEST_GUESS";
})(ForecastFrequency || (ForecastFrequency = {}));
export const baseForecastConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:BaseForecastConfig',
    type: 'object',
    properties: {
        dateColumn: { type: 'string', minLength: 1 },
        valueColumn: { type: 'string', minLength: 1 },
        alias: { type: 'string', minLength: 1 },
        periods: { type: 'integer', minimum: 1 },
        frequency: {
            type: 'string',
            enum: Object.values(ForecastFrequency)
        },
        groupByColumns: {
            type: 'array',
            items: { type: 'string' },
            uniqueItems: true
        },
        uncertainty: { type: 'boolean' },
        ignoreLimit: { type: 'boolean' },
        isMultiVariate: { type: 'boolean' },
        staticAttributes: {
            type: 'array',
            items: { type: 'string' },
            uniqueItems: true
        },
        excludeColumns: {
            type: 'array',
            items: { type: 'string' },
            uniqueItems: true
        },
        smartExclude: { type: 'boolean' },
        performanceMeasureMethod: {
            type: 'string',
            enum: Object.keys(RegressorPerformanceMeasureMethod).map((key) => RegressorPerformanceMeasureMethod[key])
        }
    },
    required: ['dateColumn', 'valueColumn', 'alias', 'periods']
};
let BaseForecastConfig = BaseForecastConfig_1 = class BaseForecastConfig extends ToolConfig {
    constructor(toolType, dateColumn, valueColumn, alias, periods, frequency, groupByColumns, ignoreLimit, isMultiVariate, staticAttributes, excludeColumns, smartExclude, performanceMeasureMethod, customFormats) {
        super(toolType, customFormats);
        this.dateColumn = dateColumn;
        this.valueColumn = valueColumn;
        this.alias = alias;
        this.periods = periods;
        this.frequency = frequency;
        this.uncertainty = true;
        this.ignoreLimit = ignoreLimit ?? false;
        this.isMultiVariate = isMultiVariate ?? false;
        this.performanceMeasureMethod = performanceMeasureMethod ?? RegressorPerformanceMeasureMethod.Automatic;
        if (staticAttributes)
            this.staticAttributes = staticAttributes;
        if (excludeColumns)
            this.excludeColumns = excludeColumns;
        if (smartExclude)
            this.smartExclude = smartExclude;
        if (groupByColumns)
            this.groupByColumns = groupByColumns;
        JSON.validate(this, BaseForecastConfig_1);
    }
    setDefaults() {
        this.frequency = ForecastFrequency.BestGuess;
        this.uncertainty = false; // forecast created in old time should have this as False
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return this.ins[0];
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return BaseForecastConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return BaseForecastConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return BaseForecastConfig_1.outs;
    }
    static get in() {
        return BaseForecastConfig_1.ins[0];
    }
    static get out() {
        return BaseForecastConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    getOutput(iConfigHelper, ins) {
        return this.getOutputForForecastConfig(ins, this.in, this.out, this.dateColumn, [this.valueColumn], [this.alias], this.uncertainty, this.groupByColumns);
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const newAddedCols = ['IsForecast'];
        if (this.uncertainty) {
            newAddedCols.push(`${this.alias} Lower`);
            newAddedCols.push(`${this.alias} Upper`);
        }
        const outputs = this.groupByColumns ? [...this.groupByColumns, ...newAddedCols] : [...newAddedCols];
        outputs.push(this.dateColumn, this.valueColumn, this.alias);
        if (new Set(outputs).size !== outputs.length) {
            if (this.groupByColumns?.includes(this.valueColumn)) {
                return ToolValidationResult.getBadMetadataResult(`Value column '${this.valueColumn}' is also in group by columns.`, `Remove '${this.valueColumn}' from group by columns or change value column. Make sure that value column and group by columns are distinct.`);
            }
            if (this.alias === this.valueColumn) {
                return ToolValidationResult.getBadMetadataResult(`Value column '${this.valueColumn}' is also in alias.`, `Change alias. Make sure that value column and alias are distinct.`);
            }
            if (this.alias === this.dateColumn) {
                return ToolValidationResult.getBadMetadataResult(`Date column '${this.dateColumn}' is also in alias.`, `Change alias. Make sure that date column and alias are distinct.`);
            }
            if (newAddedCols.includes(this.alias)) {
                return ToolValidationResult.getBadMetadataResult(`Alias '${this.alias}' is also in new added columns. New added column names are reserved for output: ${newAddedCols.join(', ')}.`, `Change alias. Make sure that alias and new added column names are distinct.`);
            }
            if (newAddedCols.includes(this.valueColumn)) {
                return ToolValidationResult.getBadMetadataResult(`Value column '${this.valueColumn}' is also in new added columns. New added column names are reserved for output: ${newAddedCols.join(', ')}.`, `Rename value column. Make sure that value column name and new added column names are distinct.`);
            }
            if (newAddedCols.includes(this.dateColumn)) {
                return ToolValidationResult.getBadMetadataResult(`Date column '${this.dateColumn}' is also in new added columns. New added column names are reserved for output: ${newAddedCols.join(', ')}.`, `Rename date column. Make sure that date column name and new added column names are distinct.`);
            }
            const duplicates = outputs.filter((item, index) => outputs.indexOf(item) !== index);
            return ToolValidationResult.getBadMetadataResult(`Duplicate columns in output: ${duplicates.join(', ')}.`, `Please Rename columns in source: ${duplicates.join(', ')}.`);
        }
        const inputs = new Map([[this.valueColumn, ['number']]]);
        if (this.dateColumn !== 'automatic')
            inputs.set(this.dateColumn, ['date']);
        this.addGroupByToInputs(inputs, this.groupByColumns);
        const sourceValidationResult = validateSourceWithDetail(source, inputs, []);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
__decorate([
    BeforeDeserialized()
], BaseForecastConfig.prototype, "setDefaults", null);
BaseForecastConfig = BaseForecastConfig_1 = __decorate([
    JsonSchemaV2(baseForecastConfigSchema, baseForecastConfigSchemaValidation)
], BaseForecastConfig);
export { BaseForecastConfig };
//# sourceMappingURL=baseForecastConfig.js.map